import { BimDto } from ".";

export enum ComponentState {
    Loading = "loading",
    Error = "error",
    Success = "success",
}

export enum TransactionStatus {
    Pending = "pending",
    Approved = "approved",
    Rejected = "rejected",
}

export enum TransactionType {
    Cancellation = "cancellation",
    Issuance = "issuance",
    Transfer = "transfer",
    Export = "export",
    Import = "import",
}

export enum EnergySource {
    Biomass = "biomass",
    Coal = "coal",
    Gas = "gas",
    Geothermal = "geothermal",
    Hydro = "hydro",
    Nuclear = "nuclear",
    Oil = "oil",
    Other = "other",
    Solar = "solar",
    Wind = "wind",
    RenewableUnknown = "renewable-unknown",
}

export enum InvoicedVolumeTypeEnum {
    InvoiceValidated = "invoice-validated",
    InvoiceProjected = "invoice-projected",
}

export const validEacEnergySources: Array<EnergySource> = [
    EnergySource.Hydro,
    EnergySource.Nuclear,
    EnergySource.Solar,
    EnergySource.Wind,
    EnergySource.Biomass,
    EnergySource.Geothermal,
];

export enum IndustryType {
    Automotive = "automotive",
    ConsumerGoods = "cgoods",
    DataCenters = "dcenters",
    Education = "education",
    FinancialServices = "financial",
    Healthcare = "healthcare",
    Manufacturing = "manufacturing",
    Office = "office",
    Pharma = "pharma",
    RealState = "restate",
    Retail = "retail",
    Telecoms = "telecomms",
}

export enum ContractType {
    VPPA = "vppa",
    PPA = "ppa",
    DirectPPA = "direct_ppa",
    GreenTariff = "green_tariff",
    UnbundledTransaction = "unbundled_eacs",
}

export enum InvoicingLevel {
    ContractLevel = "contract_level",
    SiteLevel = "site_level",
}

export enum CfeDiagnosticContractType {
    PPA = "ppa",
    GreenTariff = "green_tariff",
    UnbundledTransaction = "unbundled_eacs",
    NonCfe = "non_cfe",
}

export enum ContractStartDate {
    Before2024 = "before-january-2024",
    After2024 = "after-january-2024",
}
export enum AssetCommissioningDate {
    Unknown = "unknown",
    Less15YearsAgo = "less-than-15-years-ago",
    EqualOrMore15YearsAgo = "15-years-ago-or-more",
}

export enum EstimationStatus {
    Pending = "pending",
    Processing = "processing",
    Ready = "ready",
}

export enum Module {
    CfeConnect = "cfe-connect",
    CfeDiagnostic = "cfe-diagnostic",
    CfeInventory = "cfe-inventory",
    Settings = "settings",
    Uploads = "uploads",
}

// CFE CONNECT

export enum CfeConnectPage {
    MeterData = "meter-data",
    Monitoring = "monitoring",
    MonitoringSiteDetails = "monitoring-site-details",
    EacsAndAttestations = "eacs-and-attestations",
    LEGACY_EacData = "eac-data",
    InvoicedGridConsumption = "invoiced-grid-consumption",
    TransactionsAllocation = "transactions-allocation",
    Alerts = "alerts",
    AlertDetails = "alert-details",
}

export type CfeConnectNavPage =
    | CfeConnectPage.MeterData
    | CfeConnectPage.Monitoring
    | CfeConnectPage.EacsAndAttestations
    | CfeConnectPage.InvoicedGridConsumption
    | CfeConnectPage.Alerts;

export const isCfeConnectPage = (p: unknown): p is CfeConnectPage =>
    Object.values(CfeConnectPage).some((v) => v === p);

export const isCfeConnectNavPage = (p: unknown): p is CfeConnectNavPage =>
    [
        CfeConnectPage.MeterData,
        CfeConnectPage.Monitoring,
        CfeConnectPage.EacsAndAttestations,
        CfeConnectPage.InvoicedGridConsumption,
        CfeConnectPage.Alerts,
    ].some((v) => v === p);

// CFE DIAGNOSTIC
export enum CfeDiagnosticPage {
    Estimations = "estimations",
    NewEstimation = "new-estimation",
    EditEstimation = "edit-estimation",
    EstimationResult = "estimation-result",
}

export const isCfeDiagnosticPage = (p: unknown): p is CfeDiagnosticPage =>
    Object.values(CfeDiagnosticPage).some((v) => v === p);

export type CfeDiagnosticNavPage = CfeDiagnosticPage.Estimations;

export const isCfeDiagnosticNavPage = (p: unknown): p is CfeDiagnosticNavPage =>
    [CfeDiagnosticPage.Estimations].some((v) => v === p);

// CFE INVENTORY
export enum CfeInventoryPage {
    TwentyFourSevenCfeDashboard = "24-7-cfe-dashboard",
    PpaFinancialDashboard = "ppa-financial-dashboard",
    ContractTracking = "contract-tracking",
    ReportingDashboard = "reporting-dashboard",
    ReportingDashboardCountry = "reporting-dashboard-country",
    AssignmentOverview = "assignment-overview",
    RegionAssignment = "region-assignment",
    EditRegionAssignment = "edit-region-assignment",
    GlobalOverview = "global-overview",
    LEGACY_GlobalOverviewV2 = "global-overview-v2",
    LEGACY_TransactionsAllocation = "transactions-allocation",
}

export type CfeInventoryNavPage =
    | CfeInventoryPage.TwentyFourSevenCfeDashboard
    | CfeInventoryPage.PpaFinancialDashboard
    | CfeInventoryPage.ReportingDashboard
    | CfeInventoryPage.ContractTracking
    | CfeInventoryPage.GlobalOverview;

export const isCfeInventoryPage = (p: unknown): p is CfeInventoryPage =>
    Object.values(CfeInventoryPage).some((v) => v === p);

export const isCfeInventoryNavPage = (p: unknown): p is CfeInventoryNavPage =>
    [
        CfeInventoryPage.TwentyFourSevenCfeDashboard,
        CfeInventoryPage.PpaFinancialDashboard,
        CfeInventoryPage.ReportingDashboard,
        CfeInventoryPage.ContractTracking,
        CfeInventoryPage.GlobalOverview,
    ].some((v) => v === p);

// SETTINGS
export enum SettingsPage {
    // USERS
    Users = "users",
    UserDetails = "user-details",
    // TENANTS
    TenantInformation = "tenant-information",
    // SITES
    Sites = "sites",
    EditSite = "edit-site",
    CreateSite = "create-site",
    // METERS
    Meters = "meters",
    // CONSUMPTION SITE GROUPS
    ConsumptionSiteGroups = "consumption-site-groups",
    // CONTRACTS
    Contracts = "contracts",
    EditContract = "edit-contract",
    CreateContract = "create-contract",
}

export type SettingsNavPage =
    | SettingsPage.Users
    | SettingsPage.TenantInformation
    | SettingsPage.Sites
    | SettingsPage.Meters
    | SettingsPage.ConsumptionSiteGroups
    | SettingsPage.Contracts;

export const isSettingsPage = (p: unknown): p is SettingsPage =>
    Object.values(SettingsPage).some((v) => v === p);

export const isSettingsNavPage = (p: unknown): p is SettingsNavPage =>
    [
        SettingsPage.Users,
        SettingsPage.TenantInformation,
        SettingsPage.Sites,
        SettingsPage.Meters,
        SettingsPage.ConsumptionSiteGroups,
        SettingsPage.Contracts,
    ].some((v) => v === p);

// UPLOADS PAGE

export enum UploadsPage {
    EacUploads = "eac-uploads",
    FileUploads = "file-uploads",
    FileUploadDetail = "file-upload-detail",
}

// AUTH PAGE

export enum AuthPage {
    Login = "login",
    Verify = "verify",
    LoginError = "login-error",
    TenantPending = "tenant-pending",
    TenantCancelled = "tenant-cancelled",
    AuthCallback = "auth-callback",
    NoDisplayOptions = "no-display-options",
}
export type Page =
    | CfeConnectPage
    | CfeDiagnosticPage
    | CfeInventoryPage
    | SettingsPage
    | UploadsPage
    | AuthPage;

export type NavPage =
    | CfeConnectNavPage
    | CfeDiagnosticNavPage
    | CfeInventoryNavPage
    | SettingsNavPage;

export const isPage = (p: unknown): p is Page =>
    isCfeConnectPage(p) || isCfeDiagnosticPage(p) || isCfeInventoryPage(p) || isSettingsPage(p);

export enum Service {
    BIM = "bim",
    CfeDiagnostic = "cfe-diagnostic",
    GridEmissions = "grid-emissions",
    MeterDataSimulation = "meter-data-simulation",
    MeterData = "meter-data",
    TwentyFourSeven = "twenty-four-seven",
    EacData = "eac-data",
    ReportHub = "report-hub",
    FinancialData = "financial-data",
    GcRegistryBridge = "gc-registry-bridge",
}

export enum ResourceAction {
    Read = "read",
    Upsert = "upsert",
    Delete = "delete",
}

export enum ZoneDataAccessLevel {
    Accessible = "Accessible",
    Moderate = "Moderate",
    Challenging = "Challenging",
    Difficult = "Difficult",
    ExtremelyDifficult = "Extremely difficult",
    NoData = "No data",
}

export enum SiteType {
    Consumer = "consumer",
    Producer = "producer",
}

export const isSiteType = (s: unknown): s is SiteType =>
    Object.values(SiteType).includes(s as SiteType);

export const isEnergySource = (s: unknown): s is EnergySource =>
    Object.values(EnergySource).includes(s as EnergySource);

export const isContractType = (c: unknown): c is ContractType =>
    Object.values(ContractType).includes(c as ContractType);

export const isZoneDataAccessLevel = (s: unknown): s is ZoneDataAccessLevel =>
    Object.values(ZoneDataAccessLevel).includes(s as ZoneDataAccessLevel);

export const isService = (s: unknown): s is Service =>
    Object.values(Service).includes(s as Service);

export const isAction = (a: unknown): a is ResourceAction =>
    Object.values(ResourceAction).includes(a as ResourceAction);

export const isIntervalMeterReadingType = (
    t: string,
): t is MeterReadingTypeEnum.IntervalConsumption | MeterReadingTypeEnum.IntervalProduction =>
    t === MeterReadingTypeEnum.IntervalConsumption || t === MeterReadingTypeEnum.IntervalProduction;

export enum MeterReadingTypeEnum {
    IntervalProduction = "interval-production",
    IntervalConsumption = "interval-consumption",
    BillingProduction = "billing-production",
    BillingConsumption = "billing-consumption",
}

export enum DataQuality {
    Validated = "validated",
    NonValidated = "non-validated",
    ProviderEstimated = "provider-estimated",
    InternalEstimated = "internal-estimated",
    Gap = "gap",
}
export const isDataQuality = (d: string): d is DataQuality => {
    return Object.values(DataQuality).some((v) => v === d);
};

export enum UploadWorkflowFileStatusEnum {
    PENDING_TO_PARSE = "pending_to_parse",
    PARSE_FAILED = "parse_failed",
    TO_REVIEW = "to_review",
    PENDING_TO_VALIDATE = "pending_to_validate",
    VALIDATION_FAILED = "validation_failed",
    VALIDATED = "validated",
    PENDING_TO_SUBMIT = "pending_to_submit",
    COMPLETED = "completed",
    SUBMIT_FAILED = "submit_failed",
    DELETED = "deleted",
}

export enum DataSubType {
    Historical = "historical",
    Projected = "projected",
}

export const isDataSubType = (d: string): d is DataSubType => {
    return Object.values(DataSubType).some((v) => v === d);
};

export enum ExpectedIntervalReadingType {
    IntervalProduction = "interval-production",
    IntervalConsumption = "interval-consumption",
    IntervalProductionAndConsumption = "interval-production-and-consumption",
    /**
     * In these cases, for each interval you will have either interval-consumption or interval production. If one of the two is populated, the other one should not yield a warning.
     */
    NetMetering = "net-metering",
    None = "none",
}

export enum ExpectedReadingGranularity {
    AnyGranularity = "any-granularity",
    FiveMinutes = "five-minutes",
    HalfHourly = "half-hourly",
    Hourly = "hourly",
    Monthly = "monthly",
    QuarterHourly = "quarter-hourly",
}

export enum ReadingPendingEventAction {
    Updated = "updated",
    Created = "created",
    Deleted = "deleted",
}

export enum SourceType {
    AnnualLocationBased = "annual-location-based",
    AnnualMarketBased = "annual-market-based",
    HourlyLocationBased = "hourly-location-based",
    HourlyMarketBased = "hourly-market-based",
    Emissionality = "emissionality",
}

export enum ProvidersEnum {
    ApiUpload = "api-upload",
    ApiUploadMonthlyGrid = "api-upload-monthly-grid",
    BigQueryGoogle = "bigquerygoogle",
    C3ntinel = "c3ntinel",
    Ces = "ces",
    CsvUpload = "csv-upload",
    MonthlyGridCsvUpload = "monthly-grid-csv-upload",
    Datadis = "datadis",
    Ealyze = "ealyze",
    Elexon = "elexon",
    Eneco = "eneco",
    Energiemissie = "energiemissie",
    Energinet = "energinet",
    Fingrid = "fingrid",
    MidAmerican = "mid-american",
    TenaskaPowerServices = "tenaska-power-services",
    UtilityAPI = "utilityapi",
    Wattson = "wattson",
    Eddie = "eddie",
    Modity = "modity",
    Semo = "semo",
    Erg = "erg",
    NWCPUD = "nwcpud",
    CEC = "cec",
    CECMetersDiscovery = "cec-meters-discovery",
    Mako = "mako",
    Zepak = "zepak",
    Neso = "neso",
    TumsNl = "tums-nl",
    BrookfieldRenewables = "brookfield-renewables",
    Statkraft = "statkraft",
    DukeEnergy = "duke-energy",
    EngieNA = "engie-na",
    Quantum = "quantum",
    Edpr = "edpr",
    Cen_prtme = "cen-prtme",
    SocoXtr = "soco-xtr",
    FingridSoap = "fingrid-soap",
    EnelNA = "enel-na",
    HistoricalMeteredToMonthlyGridConsumption = "historical-metered-to-monthly-grid-consumption",
}

export enum EacProvidersEnum {
    CsvUpload = "csv-upload",
    Cnmc = "cnmc",
    Evident = "evident",
    Cer = "cer",
}

export enum EacDataUploadWorkflowExecutorIdsEnum {
    evidentAiParser = "evident-ai-parser",
    passTroughCancellationsUpload = "pass-through-cancellations-upload",
    genericAiParser = "generic-ai-parser",
    genericAiParserExperimental = "generic-ai-parser-experimental",
    necsCancellationsParser = "necs-cancellations-parser",
    genericAiParserExperimentalV2 = "generic-ai-parser-experimental-v2",
}

export enum EacBatchesStatusEnum {
    AVAILABLE = "available",
    CANCELLED = "cancelled",
    PENDING = "pending",
}

/** Database ids of EAC registries in EAC Data */
export enum EacRegistryEnum {
    Vector = "vector",
    Recsgse = "recsgse",
    MyVerticer = "myverticer",
    GRexDenmark = "g-rex.denmark",
    Ofgem = "ofgem",
    Cesar = "cesar",
    FinExtra = "finextra",
    pjmGats = "pjm-gats",
    pjmGatsGc = "pjm-gats-gc",
    ercot = "ercot",
    wregis = "wregis",
    eex = "eex",
    evident = "evident",
    Atos = "atos",
    Creg = "creg",
    Brugel = "brugel",
    Vreg = "vreg",
    ECwape = "e-cwape",
    Pronovo = "pronovo",
    TsoCy = "tso-cy",
    Ote = "ote",
    Elering = "elering",
    Lagie = "lagie",
    GRexHungary = "g-rex.hungary",
    CmoGrexel = "cmo.grexel",
    CmoIceland = "cmo.iceland",
    GRexLithuania = "g-rex-lithuania",
    GRexLuxembourg = "g-rex-luxembourg",
    GRexLatvia = "g-rex.latvia",
    Necs = "necs",
    Eego = "eego",
    PoiBorzen = "poi.borzen.si",
    Okte = "okte",
    MRets = "m-rets",
    MRetsGc = "m-rets-gc",
    NarApx = "nar-apx",
    NarApxGc = "nar-apx-gc",
    NcRets = "nc-rets",
    Nvtrec = "nvtrec",
    Nzecs = "nzecs",
    Tge = "tge",
    Gec = "gec",
    Tigr = "tigr",
    Seda = "seda",
    Trec = "t-rec",
    Nygats = "nygats",
    Carbonzero = "carbonzero",
    Anre = "anre",
    Cer = "cer",
    MirecsApx = "mirecs-apx",
    NepoolGis = "nepool-gis",
    Cel = "cel",
    CerCne = "cer-cne",
    GRexAlbania = "g-rex.albania",
    Uba = "uba",
    Rews = "rews",
    GRexSerbia = "g-rex.serbia",
    YekG = "yek-g",
    Nzec = "nzec",
    Jepx = "jepx",
    GecJp = "gec-jp",
    JCredit = "j-credit",
    KRe100 = "k-re100",
    CertigyHr = "certigy-hr",
}
export const isEacRegistryId = (e: string): e is EacRegistryEnum =>
    Object.values(EacRegistryEnum).includes(e as EacRegistryEnum);

export enum EacCodeUseTypeEnum {
    ConsumptionSite = "consumption-site", // If the eac code is for a consumption site
    ProductionSite = "production-site", // If the eac code is for a production site
    Beneficiary = "beneficiary", // If the eac code is for a whole organization/tenant/beneficiary
    CancellationParty = "cancellation-party", // If the eac code is for the cancellationParty / comercializadora
}

export enum MatchingLogic {
    PayAsConsumed = "pay-as-consumed",
    PayAsProduced = "pay-as-produced",
}

export const isMatchingLogic = (m: unknown): m is MatchingLogic =>
    Object.values(MatchingLogic).includes(m as MatchingLogic);

export enum RoleContractSite {
    Consumer = "consumer",
    Producer = "producer",
}

export enum EmissionsType {
    LocationBased = "location-based",
    MarketBased = "market-based",
}

export enum CfeCoverageMetricsEnum {
    ContractedCfe = "contracted-cfe",
    ExcessCfe = "excess-cfe",
    GridMixCfe = "grid-mix-cfe",
    GridMixNonCfe = "grid-mix-non-cfe",
}

// How to not have this here - should only be defined in BIM
// ../api-client/src/schemas/bim-api-schema.ts cannot import from BIM
export enum TenantStatus {
    Approved = BimDto.TenantStatus.Approved,
    Pending = BimDto.TenantStatus.Pending,
    Rejected = BimDto.TenantStatus.Rejected,
    Cancelled = BimDto.TenantStatus.Cancelled,
}

export enum SourcingMethod {
    DefaultRenewableElectricityFromGrid95NoAllocations = "default_renewable_electricity_from_grid_95_no_allocations",
    DefaultRenewableElectricityFromGridWithEacs = "default_renewable_electricity_from_grid_with_eacs",
    DirectLinePpa = "direct_line_ppa",
    OnSitePpa = "on_site_ppa",
    PhysicalPpa = "physical_ppa",
    ProjectSpecificContract = "project_specific_contract",
    RetailGreenElectricity = "retail_green_electricity",
    UnbundledEacs = "unbundled_eacs",
    VirtualPpa = "virtual_ppa",
    Unknown = "unknown",
}

export enum TrackingInstrumentForAssignmentType {
    Attestation = "attestation",
    EAC = "eac",
    Contract = "contract",
}

export enum TrackingInstrumentType {
    Attestation = "attestation",
    EAC = "eac",
}

export enum TrackingInstrumentTypeWithGC {
    Attestation = "attestation",
    EAC = "eac",
    GC = "gc",
}

export enum AllocationStatus {
    Allocated = "allocated",
    Unallocated = "unallocated",
}

export enum CompletenessWarning {
    MissingReadings = "MissingReadings",
    IncompleteReadings = "IncompleteReadings",
}

export enum DataGranularity {
    Hourly = "hourly",
    Daily = "daily",
    Monthly = "monthly",
}

export enum SiteLevelWarning {
    Outlier = "Outlier",
    Staleness = "Staleness",
    ExceededInstalledCapacity = "ExceededInstalledCapacity",
}

export enum SanityWarning {
    QuantityNotPositive = "QuantityNotPositive",
    QuantityNotNumber = "QuantityNotNumber",
    StartTimeNotValidDate = "StartTimeNotValidDate",
    EndTimeNotValidDate = "EndTimeNotValidDate",
    StartTimeGreaterThanEndTime = "StartTimeGreaterThanEndTime",
    StartTimeNotMultipleOf5 = "StartTimeNotMultipleOf5",
    EndTimeNotMultipleOf5 = "EndTimeNotMultipleOf5",
    OverlappingReadings = "OverlappingReadings",
    QuantityEstimated = "QuantityEstimated",
    BadFormattedReading = "BadFormattedReading",
    MissingReading = "MissingReading",
    MissingQuantity = "MissingQuantity",
    ConnectionError = "ConnectionError",
    BadTimestamps = "BadTimestamps",
    GranularityForIntervalNotSupported = "GranularityForIntervalNotSupported",
    GranularityForBillingNotSupported = "GranularityForBillingNotSupported",
}

export enum GlobalRegion {
    EMEA = "EMEA",
    APAC = "APAC",
    NA = "NA",
    LATAM = "LATAM",
}
export enum FinancialPerformanceMethodology {
    DeprecatedNetCashflowAverageHourly = "net-cashflow-average-hourly",
    DeprecatedNetCashflowAverageDaily = "net-cashflow-average-daily",
    KcVppaCumberhead = "kc-vppa-cumberhead",
    KcVppaCumberheadBm = "kc-vppa-cumberhead-bm",
    KcVppaSolarAf = "kc-vppa-solar-af",
    PgVppaEu = "pg-vppa-eu",
    PGVppaUSASunValleySolar = "pg-vppa-usa-sun-valley-solar",
    PGVppaUSATylerBluff = "pg-vppa-usa-tyler-bluff",
}

export enum Units {
    GramsCO2PerKWh = "gCO₂/KWh",
    Tonnes = "t",
    Wh = "Wh",
    KWh = "KWh",
    MWh = "MWh",
    Percent = "%",
    HourlyReadings = "hourly readings",
    Sites = "sites",
    Alerts = "alerts",
}

export enum PowerExchangesEnum {
    // Projected Power Exchanges
    NordPool = "nordpool",
    EpexSpot = "epex_spot",
    TGE = "tge",
    OMIE = "omie",
    Ercot = "ercot",
    GME = "gme",
    CsvUpload = "csv-upload",
}

export enum MarketGranularityEnum {
    Hourly = "hourly",
    HalfHourly = "half-hourly",
    QuarterHourly = "quarter-hourly",
}

export enum DataCompletenessEnum {
    AllDataReceived = "all-data-received",
    PartialDataReceived = "partial-data-received",
    NoDataReceived = "no-data-received",
}

export enum EacRegistryType {
    EAC = "eac",
    GC = "gc",
}

export enum EacProcessingStatus {
    Pending = "pending",
    Submitted = "submitted",
    Processed = "processed",
    Failed = "failed",
}

export enum DeviceTechnologyType {
    SolarPhotovoltaic = "solar_photovoltaic",
    SolarConcentration = "solar_concentration",
    SolarUnspecified = "solar_unspecified",
    WindOffshore = "wind_offshore",
    WindOnshore = "wind_onshore",
    WindUnspecified = "wind_unspecified",
    HydroUnspecified = "hydro_unspecified",
    GeothermalUnspecified = "geothermal_unspecified",
}

export enum AlertStatus {
    Created = "created",
    InProgress = "in-progress",
    Solved = "solved",
    Acknowledged = "acknowledged",
}

export enum AlertPoliciesEnum {
    DataCompleteness75to95 = "data-completeness-75-to-95",
    DataCompletenessBelow75 = "data-completeness-below-75",
    DataDelayUnavailable = "data-delay-unavailable",
    DataDelay1to30 = "data-delay-1-to-30",
    DataDelayOver30 = "data-delay-over-30",
}

export enum PolicySeverity {
    Low = "low",
    Medium = "medium",
    Critical = "critical",
}
